// 1. RELATION TYPE CONSTANTS (KEPT ORIGINAL)
export const RelationType = {
  FATHER: 'Father',
  MOTHER: 'Mother',
  SON: 'Son',
  DAUGHTER: 'Daughter',
  BROTHER: 'Brother',
  SISTER: 'Sister',
  SPOUSE: 'Spouse',
  SIBLING: 'Sibling',
  CHILD: 'Child',
  PARENT: 'Parent',
  SELF: 'Self',
  GRANDFATHER: 'Grandfather',
  GRANDMOTHER: 'Grandmother',
  GRANDSON: 'Grandson',
  GRANDDAUGHTER: 'Granddaughter',
  UNCLE: 'Uncle',
  AUNT: 'Aunt',
  NEPHEW: 'Nephew',
  NIECE: 'Niece',
  COUSIN: 'Cousin',
  BROTHER_IN_LAW: 'Brother-in-Law',
  SISTER_IN_LAW: 'Sister-in-Law',
  CO_BROTHER: 'Co-Brother',
  CO_SISTER: 'Co-Sister',
  FATHER_IN_LAW: 'Father-in-Law',
  MOTHER_IN_LAW: 'Mother-in-Law',
  SON_IN_LAW: 'Son-in-Law',
  DAUGHTER_IN_LAW: 'Daughter-in-Law',
  GREAT_GRANDFATHER: 'Great-Grandfather',
  GREAT_GRANDMOTHER: 'Great-Grandmother',
} as const;

export type RelationType = typeof RelationType[keyof typeof RelationType] | string;

export interface Comment {
  id: string;
  authorId: string;
  text: string;
  timestamp: string;
}

export type PostCategory = 'status' | 'event' | 'capsule';
export type PostPrivacy = 'public' | 'specific' | 'private';

export interface EventDetails {
  title: string;
  date: string;
  location: string;
}

export interface PostRSVP {
  attending: string[];
  maybe: string[];
  declined: string[];
}

export interface Post {
  // --- CORE FIELDS ---
  id: string;
  authorId: string;           // Member ID (for UI/family relationships)
  firebaseUid: string;        // Firebase UID (for security validation in Firestore rules)
  familyId: string;           // Family ID (for filtering by family)
  content: string;
  timestamp: string;
  type: PostCategory;         // 'status' | 'event' | 'capsule'
  privacy: PostPrivacy;       // 'public' | 'specific' | 'private'
  
  // --- MEDIA ---
  imageUrl?: string;
  mediaType?: 'image' | 'video';
  videoUrl?: string;          // Deprecated, use imageUrl + mediaType
  
  // --- AUDIENCE TARGETING ---
  targetAudienceIds: string[]; // Member IDs who can see this post (if privacy === 'specific')
  
  // --- COMMENTS & INTERACTIONS ---
  comments: number;
  commentsList?: Comment[];
  likes: string[];            // Array of member IDs who liked
  likedBy?: string[];         // Deprecated, use likes
  reactions?: Record<string, string>;
  
  // --- EVENT-SPECIFIC (type === 'event') ---
  eventDetails?: EventDetails;
  rsvp?: PostRSVP;            // { attending: [], maybe: [], declined: [] }
  
  // --- TIME CAPSULE-SPECIFIC (type === 'capsule') ---
  unlockDate?: string;
  isLocked?: boolean;
  isPinned?: boolean; // If true, receiver wants it pinned at top
  
  // --- OPTIONAL / DEPRECATED FIELDS ---
  taggedMemberIds?: string[];
  aiPolished?: boolean;
  memoryYear?: string;
  isWished?: boolean;
  isReported?: boolean;
  reportReason?: string;
  isSystemBroadcast?: boolean;
  acknowledgements?: string[];
}

// --- NETWORK CONNECTION ---
export interface Connection {
    userId: string;
    timestamp: string;
    tags: string[];
}

export interface FamilyMember {
  id: string;
  name: string;
  relation: string;
  gender: 'Male' | 'Female' | 'Other';
  dob: string;
  
  // --- FAMILY ISOLATION ---
  familyId: string;  // CRITICAL: All members must belong to a family
  
  // --- MARRIAGE FIELDS ---
  maritalStatus?: 'Single' | 'Married' | 'Divorced'; // New: replaces isMarried
  isMarried?: boolean; // DEPRECATED: kept for backward compatibility
  weddingDate?: string; // Stored as YYYY-MM-DD
  divorceDate?: string; // Stored as YYYY-MM-DD (divorce date if divorced)
  demiseDate?: string; // Stored as YYYY-MM-DD
  
  avatarUrl: string;
  
  // --- GRAPH LINKS (Essential for Tree Visualization) ---
  spouseId?: string;      // Primary spouse ID for tree
  spouseIds?: string[];   // For multiple/past spouses
  
  // Note: We keep parentIds for your existing logic, 
  // but add fatherId/motherId for the new Tree Graph efficiency
  parentIds?: string[];
  fatherId?: string;      // <--- NEW: Needed for Tree Graph
  motherId?: string;      // <--- NEW: Needed for Tree Graph
  
  childrenIds?: string[];
  
  // --- PROFILE FIELDS ---
  isDeceased?: boolean;
  nativePlace?: string;
  location?: string;
  motherTongue?: string;
  bloodGroup?: string;
  profession?: string;
  education?: string;
  email?: string;
  phoneNumber?: string;
  bio?: string;
  almaMater?: string;
  hobbies?: string[];
  hideContactDetails?: boolean;
  joined?: boolean;
  createdBy?: string;
  role?: 'admin' | 'user';
  status?: 'active' | 'suspended';
  
  // --- STORY FIELDS ---
  storyUrl?: string;
  storyType?: 'image' | 'video';
  storyTimestamp?: string;
  
  // --- NETWORK FIELD ---
  connections?: Connection[];
}

// --- NOTIFICATIONS ---
export interface Notification {
  id: string;
  recipientId: string;
  actorId: string;
  actorName: string;
  actorAvatar?: string;
  timestamp: string;
  familyId: string; // CRITICAL: For family-level isolation
  
  type: 'like' | 'comment' | 'invite' | 'birthday' | 'wedding' | 'event' | 'member_joined' | 'admin_alert';
  title: string;
  message: string;
  
  targetId?: string;
  targetType?: 'post' | 'member' | 'event';
  actionUrl?: string;
  
  read: boolean;
  dismissed: boolean;
  
  priority: 'low' | 'normal' | 'high';
  pushSent?: boolean;
  emailSent?: boolean;
}

export interface NotificationPreferences {
  userId: string;
  likes: boolean;
  comments: boolean;
  memberJoined: boolean;
  birthdayReminders: boolean;
  eventReminders: boolean;
  adminAlerts: boolean;
  emailNotifications: boolean;
  pushNotifications: boolean;
  quietHours?: { start: string; end: string };
}

// --- MESSAGING TYPES ---
export interface Conversation {
  id: string;
  participants: string[]; // Array of user IDs
  participantNames: { [userId: string]: string }; // Map of userId to displayName
  participantAvatars: { [userId: string]: string }; // Map of userId to avatar URL
  lastMessage?: string;
  lastMessageTime?: string;
  lastMessageSenderId?: string;
  unreadCount: { [userId: string]: number }; // Unread messages per user
  createdAt: string;
  updatedAt: string;
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderAvatar?: string;
  content: string;
  timestamp: string;
  read: boolean;
  edited: boolean;
  editedAt?: string;
  attachments?: {
    type: 'image' | 'file' | 'link';
    url: string;
    name: string;
  }[];
  reactions?: { [emoji: string]: string[] }; // emoji -> array of user IDs who reacted
}